<template>
  <div class="aircraft-form">
    <toolbar
      backRoute="/dashboard/aircrafts"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="aircraft-item-delete"
      saveButtonLabel="Save aircraft"
      saveButtonEvent="aircraft-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="aircraft-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>Operator:</label>

                <validation-provider
                  rules="required"
                  name="customer_id"
                  v-slot="{ errors }">
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      :options="operators"
                      v-model="data.customer_id"
                      :reduce="customer => customer.id"
                      :filterBy="filterByCustomers"
                      :maxItemsDisplay="100"
                      placeholder="Select an operator"
                      label="id">
                      <template slot="no-options">
                        <template>There are no operators for the provided phrase&hellip;</template>
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="icao_type">ICAO type:</label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.icao_type"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="long_name">Long name:</label>
                <d-input
                  v-model="data.long_name" />
              </div>

              <div class="form-group">
                <label for="mtow">MTOW (kg):</label>
                <d-input v-model="data.mtow" />
              </div>

              <div class="form-group">
                <label for="hours">Regn mark:</label>

                <validation-provider
                  rules="required"
                  name="regin_mark"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.regin_mark"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="seats">Seats:</label>
                <d-input
                  class="input-type-for-firefox"
                  type="number"
                  v-model="data.seats" />
              </div>

              <div class="form-group">
                <label for="usual_base">Usual base:</label>
                <d-input v-model="data.usual_base" />
              </div>

              <div class="form-group">
                <a :href="aircraft_api_panel" target="_blank">Aircraft Schedules - click here</a>
              </div>

              <div class="form-group">
                <label for="usual_base">Aircraft panel password (set to change previous password):</label>
                  <d-input
                    v-model="aircraft_api_password" />
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="notes">Flight type:</label>
                <div :class="{ 'form-control': true, 'is-select': true }">
                  <v-select
                    :options="[
                      { text: 'Scheduled Air Service', value: 'S' },
                      { text: 'Non-scheduled Air Transport', value: 'N' },
                      { text: 'General Aviation/Owner', value: 'G' },
                      { text: 'Military', value: 'M' },
                      { text: 'Other', value: 'X' }
                    ]"
                    :clearable="false"
                    v-model="data.flight_type"
                    :reduce="option => option.value"
                    label="text">
                  </v-select>
                </div>
              </div>

              <div class="form-group align-flex-end">
                <d-checkbox
                  inline
                  v-model="data.is_archive"
                  toggle
                  toggle-small
                  class="mb-0 mt-0">
                  Is archived?
                </d-checkbox>
              </div>

              <div class="form-group">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  style="height: 114px;"
                  :rows="5" />
              </div>

              <div class="form-group">
                <label for="notes">Reason for visit EU:</label>
                <d-textarea
                  v-model="data.reason_for_visit_eu"
                  :value.prop="data.reason_for_visit_eu"
                  :rows="3" />
              </div>

              <div class="form-group">
                <label for="mtow">Call sign:</label>
                <d-input v-model="data.call_sign" />
              </div>

              <div class="form-group">
                <label for="crew_phone">Crew phone:</label>
                <d-input
                  v-model="data.crew_phone" />
              </div>
            </d-col>
          </d-row>

          <fieldset class="pt-4 pb-4 mb-4 mt-4">
            <legend>Aircraft details</legend>
            <d-row>
              <d-col cols="4">
                <d-checkbox
                  inline
                  v-model="data.less_8000_mtow"
                  toggle>
                  Less than 8000 MTOW
                </d-checkbox>
              </d-col>

              <d-col cols="4">
                <d-checkbox
                  inline
                  v-model="data.uk_vat_paid"
                  toggle>
                  UK VAT paid
                </d-checkbox>
              </d-col>

              <d-col cols="4">
                <d-checkbox
                  inline
                  v-model="data.free_circulation_uk"
                  toggle>
                  Free circulation UK
                </d-checkbox>
              </d-col>
            </d-row>
          </fieldset>

          <fieldset>
            <legend>Reminders</legend>
            <d-row>
              <d-col cols="6">
                <div class="form-group">
                  <label for="insurance">Insurance:</label>
                  <d-datepicker
                    v-model="data.insurance"
                    format="dMMMyy"
                    placeholder="Select a date" />
                </div>

                <div class="form-group">
                  <label for="radio_licence">Radio licence:</label>
                  <d-datepicker
                    v-model="data.radio_licence"
                    format="dMMMyy"
                    placeholder="Select a date" />
                </div>
              </d-col>

              <d-col cols="6">
                <div class="form-group">
                  <label for="certificate_airworthiness">Certificate of airworthiness:</label>
                  <d-datepicker
                    v-model="data.certificate_airworthiness"
                    format="dMMMyy"
                    placeholder="Select a date" />
                </div>

                <div class="form-group">
                  <label for="maintenance">Maintenance:</label>
                  <d-datepicker
                    v-model="data.maintenance"
                    format="dMMMyy"
                    placeholder="Select a date" />
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <warnings-list
        v-if="loadedData"
        referenceType="AIRCRAFT"
        :referenceId="data.id"
        itemName="aircraft" />

      <checklist-list
        v-if="loadedData"
        referenceType="AIRCRAFT"
        :referenceId="data.id"
        itemName="aircraft" />
    </d-container>
  </div>
</template>

<script>
import ChecklistList from './../form-parts/CheckLists.vue';
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import ListUtils from './../../utils/ListUtils.js';
import WarningsList from './../form-parts/Warnings.vue';

export default {
  name: 'aircraft-form',
  mixins: [
    HasChangesDetection
  ],
  components: {
    'checklist-list': ChecklistList,
    'warnings-list': WarningsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedOperators;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new aircraft';
      }

      return 'Edit aircraft';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Delete aircraft';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        icao_type: '',
        long_name: '',
        mtow: '',
        regin_mark: '',
        seats: '',
        usual_base: '',
        notes: '',
        is_archive: false,
        call_sign: '',
        crew_phone: '',
        insurance: '',
        radio_licence: '',
        certificate_airworthiness: '',
        maintenance: '',
        customer_id: null,
        reason_for_visit_eu: '',
        less_8000_mtow: false,
        uk_vat_paid: false,
        free_circulation_uk: false,
        flight_type: 'G',
      },
      aircraft_api_panel: '',
      aircraft_api_password: '',
      loadError: false,
      loadedData: false,
      operators: [],
      loadedOperators: false,
      removeInProgress: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.loadAdditionalData();
    this.$bus.$on('aircraft-item-delete', this.removeAircraft);
    this.$bus.$on('aircraft-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/aircrafts/item/',
        instanceFields: ['id', 'icao_type', 'long_name', 'mtow', 'regin_mark', 'customer_id', 'seats', 'is_archive', 'notes', 'usual_base', 'call_sign', 'insurance', 'radio_licence', 'certificate_airworthiness', 'maintenance', 'reason_for_visit_eu', 'crew_phone', 'flight_type'],
        loadedKey: 'loadedData',
        successAction: (response) => {
          this.aircraft_api_panel = response.aircraft_api_panel;
          this.data.less_8000_mtow = !!response.less_8000_mtow;
          this.data.is_archive = !!response.is_archive;
          this.data.uk_vat_paid = !!response.uk_vat_paid;
          this.data.free_circulation_uk = !!response.free_circulation_uk;
          this.data = DateUtils.groupConversionStringToDate(this.data, ['insurance', 'radio_licence', 'certificate_airworthiness', 'maintenance']);
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/items',
        params: {
          pagination: 0,
          where: {
            is_operator: 1
          }
        },
        loadedKey: 'loadedOperators',
        outputKey: 'operators',
        errorKey: 'loadError'
      });
    },
    removeAircraft () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/aircrafts/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Aircraft removed',
        successText: 'The selected aircraft has been removed',
        confirmTitle: 'Do you really want to remove this aircraft?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/aircrafts');
          this.$bus.$emit('calendar-aircrafts-update');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['aircraft-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/aircrafts/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/aircrafts/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      this.data = DateUtils.groupConversionDateToString(this.data, ['insurance', 'radio_licence', 'certificate_airworthiness', 'maintenance']);
      let dataToSend = JSON.parse(JSON.stringify(this.data));

      if (this.aircraft_api_password) {
        dataToSend.aircraft_api_password = this.aircraft_api_password;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: dataToSend,
        successTitle: mode === 'add' ? 'Aircraft added' : 'Aircraft saved',
        successText: 'The aircraft has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/aircrafts');
          this.$bus.$emit('calendar-aircrafts-update');
        }
      });
    },
    filterByCustomers (option, label, search) {
      return option.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    }
  },
  beforeDestroy () {
    this.$bus.$off('aircraft-item-delete', this.removeAircraft);
    this.$bus.$off('aircraft-item-save', this.validateForm);
  }
}
</script>

<style lang="scss" scoped>
.aircraft-form {
  /deep/ .vs__dropdown-toggle {
    border: 1px solid #ddd!important;
  }
}
</style>
